// Service public — signature d'un contrat de sous-traitance (issue #37).
// Aucune authentification : appels fetch directs (miroir de
// public-job-description.ts). Le backend sert le SNAPSHOT figé au moment de
// l'envoi, jamais la BDD courante (parité avec l'acceptation publique de devis).
//
// Path mapping (cf. spec §4.2) :
// - SPA route : `/vc/:token` (page React, chunk app-public)
// - API JSON  : `/api/v1/public/vendor-contracts/:token`
// Le service de vérification du certificat vit sur un token distinct :
// `/api/v1/public/vendor-contracts/verify/:verify_token` (réponse zéro-PII).

import { PUBLIC_API_BASE, publicGet, publicPost } from "./public-fetch"

const API_PATH = "/api/v1/public/vendor-contracts"

export type PublicVendorContractStatus =
  | "PENDING_SIGNATURE"
  | "SIGNED"
  | "REFUSED"
  | "REVOKED"
  | "SIGNATURE_EXPIRED"

// Shape stable consommé par la page React (flatten du PublicContractPayload).
export interface PublicVendorContractResponse {
  status: PublicVendorContractStatus
  contract_ref: string
  version_number: number
  expires_at: string | null
  signed_at: string | null
  tenant: {
    name: string
    logo_url: string | null
  }
  vendor: {
    name: string
    siret: string | null
    legal_representative: string | null
  }
  signer_email: string
  pdf_url: string | null
  legal_notice: string
}

// Payload brut renvoyé par le backend (PublicContractPayload,
// handlers/public_vendor_contract.rs:46). Le snapshot porte les détails
// d'identité du sous-traitant figés à l'envoi.
interface RawPublicContractPayload {
  contract_id: string
  tenant_name: string
  vendor_name: string
  contract_ref: string
  version_number: number
  status_code: string
  snapshot: RawSnapshot | null
  snapshot_sha256: string
  snapshot_at: string | null
  signer_email: string
  token_expires_at: string
  already_signed: boolean
  signed_at: string | null
  legal_notice: string
}

interface RawSnapshot {
  tenant?: { name?: string | null; logo_url?: string | null } | null
  vendor?: {
    name?: string | null
    siret?: string | null
    legal_representative?: string | null
  } | null
}

function toResponse(raw: RawPublicContractPayload): PublicVendorContractResponse {
  const snap = raw.snapshot ?? {}
  const vendorSnap = snap.vendor ?? {}
  const tenantSnap = snap.tenant ?? {}
  return {
    status: (raw.status_code as PublicVendorContractStatus) ?? "PENDING_SIGNATURE",
    contract_ref: raw.contract_ref,
    version_number: raw.version_number,
    expires_at: raw.token_expires_at ?? null,
    signed_at: raw.signed_at ?? null,
    tenant: {
      name: raw.tenant_name || tenantSnap.name || "Organisme de formation",
      logo_url: tenantSnap.logo_url ?? null,
    },
    vendor: {
      name: raw.vendor_name || vendorSnap.name || "Sous-traitant",
      siret: vendorSnap.siret ?? null,
      legal_representative: vendorSnap.legal_representative ?? null,
    },
    signer_email: raw.signer_email,
    // Flag d'affichage du bouton « Lire le PDF » : l'URL réelle est construite
    // par publicVendorContractPdfUrl(token) côté page (le token n'est pas dans
    // le payload). On expose une valeur non-null pour activer le bouton.
    pdf_url: "available",
    legal_notice: raw.legal_notice,
  }
}

export interface SignPublicVendorContractResponse {
  status: PublicVendorContractStatus
  signed_at: string
}

export interface RefusePublicVendorContractResponse {
  status: PublicVendorContractStatus
}

// Réponse zéro-PII du certificat (cf. spec §4.3). Confirme l'intégrité du PDF
// que le porteur détient déjà — aucun nom, email ou IP.
export interface VerifyVendorContractResponse {
  valid: boolean
  contract_ref: string | null
  signed_at: string | null
  pdf_sha256: string | null
}

export function getPublicVendorContract(token: string): Promise<PublicVendorContractResponse> {
  return publicGet<RawPublicContractPayload>(`${API_PATH}/${token}`).then(toResponse)
}

export function publicVendorContractPdfUrl(token: string): string {
  return `${PUBLIC_API_BASE}${API_PATH}/${token}/pdf`
}

export function signPublicVendorContract(
  token: string,
  signerName: string,
): Promise<SignPublicVendorContractResponse> {
  return publicPost<SignPublicVendorContractResponse>(`${API_PATH}/${token}/sign`, {
    signer_name: signerName,
    consent: true,
  })
}

export function refusePublicVendorContract(
  token: string,
  reason: string,
): Promise<RefusePublicVendorContractResponse> {
  return publicPost<RefusePublicVendorContractResponse>(`${API_PATH}/${token}/refuse`, {
    reason,
  })
}

export function verifyVendorContract(verifyToken: string): Promise<VerifyVendorContractResponse> {
  return publicGet<VerifyVendorContractResponse>(`${API_PATH}/verify/${verifyToken}`)
}
